/**
 * Estado das anotações manuais da mesa com salvamento otimista (E36.F3). // ux-neutrality:allow doc-comment
 *
 * Carrega as anotações da análise no mount/troca de tab e expõe operações // ux-neutrality:allow doc-comment
 * otimistas: a UI reflete o gesto imediatamente e a persistência acontece em // ux-neutrality:allow doc-comment
 * background; falha reverte o estado local e liga `saveError` (a toolbar // ux-neutrality:allow doc-comment
 * exibe o aviso). `tool` é imutável — mover envia só `geometry`. // ux-neutrality:allow doc-comment
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiClient } from '@/lib/api';
import {
  adotarMesaAnnotation,
  createMesaAnnotation,
  deleteMesaAnnotation,
  importarTriagemAnnotations,
  listMesaAnnotations,
  updateMesaAnnotation,
  type AnnotationColor,
  type AnnotationGeometryData,
  type AnnotationTool,
  type MesaAnnotationData,
} from '@/lib/mesa_annotations';

export interface UseMesaAnnotationsResult {
  annotations: MesaAnnotationData[];
  /** Última operação de persistência falhou (limpo na próxima que conclui). */
  saveError: boolean;
  draw: (tool: AnnotationTool, color: AnnotationColor, geometry: AnnotationGeometryData) => void;
  move: (id: string, geometry: AnnotationGeometryData) => void;
  remove: (id: string) => void;
  /**
   * O perito assume a sugestão da análise preliminar como sua (E47.F3): ela
   * passa a ser anotação dele e, só então, pode entrar no laudo.
   */
  adotar: (id: string) => void;
}

let tempSeq = 0;

export function useMesaAnnotations(caseId: string, analysisId: string): UseMesaAnnotationsResult {
  const client = useApiClient();
  const [annotations, setAnnotations] = useState<MesaAnnotationData[]>([]);
  const [saveError, setSaveError] = useState(false);
  // Troca de tab no meio de um request em voo: descarta a resposta antiga.
  const generationRef = useRef(0);

  useEffect(() => {
    generationRef.current += 1;
    const generation = generationRef.current;
    setAnnotations([]);
    // E47.F3: a mesa puxa as marcações da análise preliminar ANTES de listar, e
    // o GET seguinte já as inclui. O import é idempotente no backend (marcador
    // durável), então montar de novo não duplica nem ressuscita o descartado —
    // e falhar não pode trancar a mesa: seguimos para o list de qualquer jeito.
    importarTriagemAnnotations(client, caseId, analysisId)
      .catch(() => undefined)
      .then(() => listMesaAnnotations(client, caseId, analysisId))
      .then((records) => {
        if (generationRef.current !== generation) return;
        // Merge, não substituição: um desenho feito enquanto a carga estava em
        // voo não pode ser apagado pela resposta do GET. Preservamos tudo que o
        // servidor não trouxe — não só os `temp-`: se o POST do desenho concluir
        // ANTES do GET, o registro local já é o do servidor (id real) e filtrar
        // por `temp-` o descartaria. A janela era estreita quando o GET era a
        // primeira chamada; com o import da triagem à frente dele (E47.F3) ela
        // alargou, e o teste do draw otimista passou a pegar isto.
        setAnnotations((current) => {
          const doServidor = new Set(records.map((r) => r.id));
          return [...records, ...current.filter((a) => !doServidor.has(a.id))];
        });
      })
      .catch(() => {
        // Falha de carga não bloqueia a mesa: ela abre sem anotações e o
        // primeiro gesto de desenho reporta erro de persistência se persistir.
      });
  }, [client, caseId, analysisId]);

  const draw = useCallback(
    (tool: AnnotationTool, color: AnnotationColor, geometry: AnnotationGeometryData): void => {
      tempSeq += 1;
      const tempId = `temp-${tempSeq}`;
      const optimistic: MesaAnnotationData = {
        id: tempId,
        case_analysis_id: analysisId,
        tool,
        color,
        geometry,
        // Quem desenha na mesa é o perito — o backend grava 'perito' de todo
        // jeito (E47.F3); o otimista só espelha isso enquanto o POST não volta.
        origem: 'perito',
        nota: null,
        usuario_id: null,
        created_at: '',
        updated_at: '',
      };
      setAnnotations((current) => [...current, optimistic]);
      createMesaAnnotation(client, caseId, analysisId, { tool, color, geometry })
        .then((record) => {
          setSaveError(false);
          setAnnotations((current) => current.map((a) => (a.id === tempId ? record : a)));
        })
        .catch(() => {
          setSaveError(true);
          setAnnotations((current) => current.filter((a) => a.id !== tempId));
        });
    },
    [client, caseId, analysisId],
  );

  const move = useCallback(
    (id: string, geometry: AnnotationGeometryData): void => {
      // Anotação otimista ainda sem id do servidor: o gesto chegou antes do
      // POST resolver — ignora (o estado local já reflete o desenho original).
      if (id.startsWith('temp-')) return;
      let previous: AnnotationGeometryData | null = null;
      setAnnotations((current) =>
        current.map((a) => {
          if (a.id !== id) return a;
          previous = a.geometry;
          return { ...a, geometry };
        }),
      );
      updateMesaAnnotation(client, caseId, analysisId, id, { geometry })
        .then((record) => {
          setSaveError(false);
          setAnnotations((current) => current.map((a) => (a.id === id ? record : a)));
        })
        .catch(() => {
          setSaveError(true);
          setAnnotations((current) =>
            current.map((a) => (a.id === id && previous ? { ...a, geometry: previous } : a)),
          );
        });
    },
    [client, caseId, analysisId],
  );

  const remove = useCallback(
    (id: string): void => {
      if (id.startsWith('temp-')) return;
      let removed: MesaAnnotationData | null = null;
      let removedIndex = 0;
      setAnnotations((current) => {
        removedIndex = current.findIndex((a) => a.id === id);
        removed = current[removedIndex] ?? null;
        return current.filter((a) => a.id !== id);
      });
      deleteMesaAnnotation(client, caseId, analysisId, id)
        .then(() => setSaveError(false))
        .catch(() => {
          setSaveError(true);
          setAnnotations((current) => {
            if (!removed) return current;
            const next = [...current];
            next.splice(Math.min(removedIndex, next.length), 0, removed);
            return next;
          });
        });
    },
    [client, caseId, analysisId],
  );

  const adotar = useCallback(
    (id: string): void => {
      if (id.startsWith('temp-')) return;
      // Sem otimismo aqui, ao contrário de draw/move/remove: adotar é o ato que
      // habilita a marcação a entrar no laudo (E47.F3) — pintá-la como assumida
      // antes da confirmação do servidor mostraria ao perito uma garantia que o
      // durável ainda não dá. Só o registro que voltou troca a camada.
      adotarMesaAnnotation(client, caseId, analysisId, id)
        .then((record) => {
          setSaveError(false);
          setAnnotations((current) => current.map((a) => (a.id === id ? record : a)));
        })
        .catch(() => setSaveError(true));
    },
    [client, caseId, analysisId],
  );

  return { annotations, saveError, draw, move, remove, adotar };
}
